import { ClientConfigurationError } from '@/lib/errors/client';

import {
  CHAIN_ID_PER_NETWORK_URL_PATH,
  NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID,
  SUPPORTED_CHAINS_BY_CHAIN_ID,
} from '@/constants/chain';

import { getBaseURL } from './environment';
import { type ChainAddress, isChainAddress, isValidAddressForChain } from './models';
import { canonicalSolanaMint, getNativeAddressFromSymbol } from './multichain';
import { normalizeTokenAddress } from './normalizeAddress';
import { TokenInfo } from './token.types';

export function getValidChainIdFromString(rawChainId: string): number {
  // 1 - Convert the full network name from the url to a valid chainId
  const chainId = CHAIN_ID_PER_NETWORK_URL_PATH[rawChainId] ?? parseInt(rawChainId, 10);

  // If the chainId is not supported, throw an error.
  if (
    typeof chainId !== 'number' ||
    Number.isNaN(chainId) ||
    !SUPPORTED_CHAINS_BY_CHAIN_ID[chainId]
  ) {
    throw new ClientConfigurationError(`Invalid chainId: ${rawChainId}`);
  }

  return chainId;
}

/**
 * Parses token metadata from scalar address and chain query parameters.
 */
export const getTokenInfoFromQueryParameter = (
  rawAddressQueryParam: string | string[] | undefined,
  rawChainIdOrNetworkNameQueryParam: string | string[] | undefined,
): TokenInfo | undefined => {
  const address =
    rawAddressQueryParam &&
    !Array.isArray(rawAddressQueryParam) &&
    isChainAddress(rawAddressQueryParam)
      ? rawAddressQueryParam
      : undefined;
  let chainId: number | undefined;

  try {
    chainId =
      rawChainIdOrNetworkNameQueryParam && !Array.isArray(rawChainIdOrNetworkNameQueryParam)
        ? getValidChainIdFromString(rawChainIdOrNetworkNameQueryParam)
        : undefined;
  } catch {
    // An invalid chain query parameter does not resolve to a token.
  }

  return address && chainId
    ? {
        address,
        chainId,
      }
    : undefined;
};

/**
 * Returns the URL for a token logo.
 * @param address - The address of the token
 * @param chainId - The chain ID of the token
 * @returns The URL for the token logo
 */
export const getTokenLogoURL = (address?: string, chainId?: number) => {
  const defaultLogoURL = getBaseURL() + '/images/chains/none.svg';
  if (address && chainId) {
    return `https://matcha-image-proxy.api.0x.org/images/${chainId}/${canonicalSolanaMint(address)}`;
  }
  return defaultLogoURL;
};

export type TokenRef = { address: string; chainId: number };

/** First path segment of a trade route; advanced trade types match their order type. */
export const TRADE_TYPES = ['swap', 'limit', 'stop-loss', 'take-profit'] as const;
export type TradeType = (typeof TRADE_TYPES)[number];

export const isTradeType = (value: unknown): value is TradeType =>
  TRADE_TYPES.includes(value as TradeType);

export const tradeRoutePathname = <T extends TradeType>(tradeType: T) =>
  `/${tradeType}/[[...tradePath]]` as const;

export type TradeRoutePathname = `/${TradeType}/[[...tradePath]]`;

/** The trade type whose page route is `pathname`; undefined on any other page. */
export const tradeTypeFromPathname = (pathname: string): TradeType | undefined =>
  TRADE_TYPES.find((tradeType) => pathname === tradeRoutePathname(tradeType));

export const parseNetworkSegment = (segment: string | undefined): number | undefined => {
  if (!segment) return undefined;
  const chainId =
    CHAIN_ID_PER_NETWORK_URL_PATH[segment] ?? (/^\d+$/.test(segment) ? Number(segment) : NaN);
  return SUPPORTED_CHAINS_BY_CHAIN_ID[chainId] ? chainId : undefined;
};

export const parseTokenPath = (
  chainSegment: string | string[] | undefined,
  contractAddresses: string | string[] | undefined,
): TokenInfo | undefined => {
  const [addressOrSymbol] = [contractAddresses ?? []].flat();
  if (typeof chainSegment !== 'string' || !addressOrSymbol) return undefined;

  const chainId = parseNetworkSegment(chainSegment);
  if (chainId === undefined) return undefined;

  const address = getNativeAddressFromSymbol(addressOrSymbol, chainId) ?? addressOrSymbol;
  if (!isValidAddressForChain(address, chainId)) return undefined;

  return { address: normalizeTokenAddress(address, chainId) as ChainAddress, chainId };
};

export const networkSegment = (chainId: number) =>
  NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID[chainId] ?? String(chainId);

export const tradePathSegments = ({ address, chainId }: TokenRef) => [
  networkSegment(chainId),
  normalizeTokenAddress(address, chainId),
];

/** Full trade route for a token; ordinary token links default to `/swap`. */
export const tradeRoute = (token: TokenRef, tradeType: TradeType = 'swap') => ({
  pathname: tradeRoutePathname(tradeType),
  query: { tradePath: tradePathSegments(token) },
});

export const tradeRouteHref = ({
  pathname,
  query: { tradePath, ...search },
}: {
  pathname: TradeRoutePathname;
  query: { tradePath?: string[] } & Record<string, string | string[] | undefined>;
}) => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    for (const item of [value ?? []].flat()) params.append(key, item);
  }
  const path = [tradeTypeFromPathname(pathname), ...(tradePath ?? [])].join('/');
  const queryString = params.toString();
  return `/${path}${queryString ? `?${queryString}` : ''}`;
};
